import {
  BALANCE,
  NEED_KINDS,
  ringOf,
  shipPayment,
  type ModuleType,
  type NeedKind,
  type ShipState,
  type StationState,
} from "@station-keeping/sim";
import { useGameUi } from "../../game/store";
import { formatClock, formatDuration, formatNumber, t } from "../../i18n";
import { cssColor, needColor } from "../../render/palette";
import { moduleName, needLabel, phaseLabel, shipLabel } from "../labels";
import { Icon, NeedIcon, PixelText } from "../pixel";
import { EmptyState, Gauge, Plate, ScoreRow, SectionTitle } from "../primitives";
import { ShipThumb } from "../thumbs";

/** Le bâtiment qui sert chaque besoin (à construire s'il manque). */
const SERVED_BY: Record<NeedKind, ModuleType> = {
  fuel: "fuel_depot",
  repair: "repair_workshop",
  rest: "cantina",
  trade: "warehouse",
};

/** Unités entières d'un besoin (la simulation compte en centièmes). */
function units(hundredths: number): number {
  return Math.floor(hundredths / 100);
}

function patienceRatio(ship: ShipState): number {
  return ship.patience / (BALANCE.ships.classes[ship.class].patienceSeconds || 1);
}

function patienceColor(ratio: number): string {
  return ratio < 0.25 ? "var(--color-alert)" : ratio < 0.5 ? "var(--color-warn)" : "var(--color-accent)";
}

/**
 * Secondes avant qu'un besoin soit servi : la capacité de la station se partage dans l'ordre d'arrivée,
 * donc les vaisseaux à quai arrivés avant passent d'abord. `null` si personne ne sert ce besoin.
 */
function serviceEta(state: StationState, ship: ShipState, kind: NeedKind): number | null {
  const capacity = state.flow.service[kind];
  if (capacity <= 0) return null;
  let ahead = 0;
  for (const other of state.ships) {
    if (other.id === ship.id) break;
    if (other.phase === "docked") ahead += Math.max(0, other.asked[kind] - other.served[kind]);
  }
  return Math.ceil((ahead + Math.max(0, ship.asked[kind] - ship.served[kind])) / capacity);
}

/** Où en est un besoin : servi, en cours (et quand), en attente du quai, ou servi par personne ici. */
function needStatus(state: StationState, ship: ShipState, kind: NeedKind): { text: string; color: string } {
  if (ship.served[kind] >= ship.asked[kind]) return { text: t("ship.need.done"), color: "var(--color-good)" };
  if (state.flow.service[kind] === 0) {
    return { text: t("ship.need.nobody", { module: moduleName(SERVED_BY[kind]) }), color: "var(--color-stop)" };
  }
  if (ship.phase !== "docked") return { text: t("ship.need.onArrival"), color: "var(--color-dim)" };
  const eta = serviceEta(state, ship, kind);
  return { text: t("ship.need.serving", { time: formatDuration(eta ?? 0) }), color: "var(--color-muted)" };
}

/** Où se trouve le vaisseau : sur l'autoroute ou en approche (vers quel poste), à quai (où), ou sur le départ. */
function whereLabel(state: StationState, ship: ShipState): string {
  const berth = ship.berth ? state.modules.find((module) => module.id === ship.berth) : undefined;
  if (!berth || ship.phase === "leaving") return phaseLabel(ship.phase);
  const ring = ringOf(berth.x, berth.y);
  if (ship.phase === "docked") {
    return berth.type === "core" ? t("ship.dockedAtCore") : t("ship.dockedAt", { module: moduleName(berth.type), ring });
  }
  const heading = berth.type === "core" ? t("ship.headingToCore") : t("ship.headingTo", { module: moduleName(berth.type), ring });
  return `${phaseLabel(ship.phase)} · ${heading}`;
}

/**
 * Fiche d'un vaisseau, ouverte en le touchant sur le plan : sa patience, chacun de ses besoins (servi ou
 * pas, par qui, dans combien de temps), et ce qu'il paiera en repartant.
 */
export function ShipPanel({ state, ship }: { state: StationState; ship: ShipState }) {
  const selectShip = useGameUi((ui) => ui.selectShip);
  const kinds = NEED_KINDS.filter((kind) => ship.asked[kind] > 0);
  const patience = patienceRatio(ship);
  const payNow = shipPayment(state, ship);
  const payFull = shipPayment(state, { ...ship, served: { ...ship.asked } });
  const counting = ship.phase === "docked";

  return (
    <div>
      <Plate title={shipLabel(ship.class)} onClose={() => selectShip(null)}>
        <p className="mt-1.5 text-[0.9375rem] text-muted">{whereLabel(state, ship)}</p>
      </Plate>

      <div className="flex justify-center py-4">
        <ShipThumb shipClass={ship.class} size="lg" />
      </div>

      {ship.phase !== "leaving" ? (
        <section aria-label={t("ship.patience")}>
          <div className="flex items-center justify-between gap-2 text-[0.9375rem]">
            <span className="flex items-center gap-2 text-ink">
              <Icon name="clock" className="text-dim" />
              {counting ? t("ship.leavesIn") : t("ship.patienceOnArrival")}
            </span>
            <span style={{ color: patienceColor(patience) }}>
              <PixelText text={formatClock(ship.patience)} label={formatDuration(ship.patience)} />
            </span>
          </div>
          <Gauge value={patience} color={patienceColor(patience)} label={t("ship.patience")} segments={16} className="mt-1.5" />
          <p className="mt-1.5 text-[0.875rem] text-dim">{t("ship.patienceHint")}</p>
        </section>
      ) : null}

      <SectionTitle hint={t("ship.needsHint")}>{t("ship.needs")}</SectionTitle>
      <ul>
        {kinds.map((kind) => {
          const color = cssColor(needColor(kind));
          const status = needStatus(state, ship, kind);
          return (
            <li key={kind} className="dotted py-2.5">
              <div className="flex items-center gap-2">
                <span style={{ color }}>
                  <NeedIcon kind={kind} />
                </span>
                <span className="flex-1 text-base font-semibold text-ink">{needLabel(kind)}</span>
                <span className="text-ink">
                  <PixelText
                    text={`${units(ship.served[kind])}/${units(ship.asked[kind])}`}
                    label={t("ship.need.amount", { served: units(ship.served[kind]), asked: units(ship.asked[kind]) })}
                  />
                </span>
              </div>
              <Gauge value={ship.served[kind] / ship.asked[kind]} color={color} label={needLabel(kind)} className="mt-1.5" />
              <p className="mt-1 text-[0.875rem]" style={{ color: status.color }}>
                {status.text}
              </p>
            </li>
          );
        })}
      </ul>

      <SectionTitle>{t("ship.payment")}</SectionTitle>
      <dl>
        <ScoreRow label={t("ship.payNow")} value={`${formatNumber(payNow)} CR`} />
        <ScoreRow label={t("ship.payFull")} value={`${formatNumber(payFull)} CR`} tone="var(--color-good)" />
      </dl>
    </div>
  );
}

/** Une ligne de besoin dans la liste : pictogramme, jauge courte, quantité servie sur demandée. */
function NeedLine({ ship, kind, unserved }: { ship: ShipState; kind: NeedKind; unserved: boolean }) {
  const color = cssColor(needColor(kind));
  return (
    <span className="flex items-center gap-2" title={unserved ? t("ship.noService") : needLabel(kind)}>
      <span style={{ color }}>
        <NeedIcon kind={kind} />
      </span>
      <span className="sr-only">{needLabel(kind)}</span>
      <Gauge value={ship.served[kind] / ship.asked[kind]} color={color} label={needLabel(kind)} segments={8} className="w-[calc(var(--px)*30)]" />
      <span className={unserved ? "text-stop" : "text-muted"}>
        <PixelText text={`${units(ship.served[kind])}/${units(ship.asked[kind])}`} label={false} />
      </span>
    </span>
  );
}

/** Les vaisseaux à quai, puis ceux qui arrivent de l'autoroute, avec leurs besoins et leur patience. */
export function ShipsPanel({ state }: { state: StationState }) {
  const openPanel = useGameUi((ui) => ui.openPanel);
  const selectShip = useGameUi((ui) => ui.selectShip);
  // À quai d'abord : ce sont eux qu'il faut servir.
  const ships = [
    ...state.ships.filter((ship) => ship.phase === "docked"),
    ...state.ships.filter((ship) => ship.phase === "highway" || ship.phase === "approach"),
  ];
  const docked = state.ships.filter((ship) => ship.phase === "docked").length;

  return (
    <div>
      <Plate title={t("ships.title")} onClose={() => openPanel(null)}>
        <p className="mt-1.5 flex items-center gap-2 text-[0.9375rem] text-ink">
          <Icon name="dock" className="text-dim" />
          {t("ships.berths", { count: docked, used: docked, total: state.flow.berths })}
        </p>
        <p className="mt-1 text-[0.9375rem] text-muted">{t("ships.highway")}</p>
        <p className="mt-1 text-[0.9375rem] text-muted">{t("ships.legend")}</p>
      </Plate>
      {ships.length === 0 ? (
        <div className="mt-3">
          <EmptyState>{t("ships.none")}</EmptyState>
        </div>
      ) : (
        <ul>
          {ships.map((ship) => {
            const patience = patienceRatio(ship);
            return (
              <li key={ship.id} className="dotted">
                <button
                  type="button"
                  onClick={() => selectShip(ship.id)}
                  className="flex w-full items-start gap-3 px-1 py-3 text-left hover:bg-face-2 focus-visible:bg-face-2"
                >
                  <span className="grid w-[4.25rem] shrink-0 place-items-center pt-1">
                    <ShipThumb shipClass={ship.class} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-base font-semibold text-ink">{shipLabel(ship.class)}</span>
                      {ship.phase !== "docked" ? null : (
                        <span style={{ color: patienceColor(patience) }} title={t("ship.leavesIn")}>
                          <PixelText text={formatClock(ship.patience)} label={t("ship.leavesInTime", { time: formatDuration(ship.patience) })} />
                        </span>
                      )}
                    </span>
                    <span className="block text-[0.875rem] text-muted">{whereLabel(state, ship)}</span>
                    <span className="mt-1.5 flex flex-col gap-1">
                      {NEED_KINDS.filter((kind) => ship.asked[kind] > 0).map((kind) => (
                        <NeedLine key={kind} ship={ship} kind={kind} unserved={state.flow.service[kind] === 0 && ship.served[kind] < ship.asked[kind]} />
                      ))}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
